'use client';

import type { SearchResult } from 'conversational-search-client';
import { type JSX, useCallback, useState } from 'react';

import { JustifiedGallery } from '@videoblocks/design-system';

import { useChatSessionContext } from '../../../_context/SessionContext';
import { useTopResults } from '../../../_hooks';
import { AllResultsSection } from '../AllResultsSection';
import { TopResultsSection } from '../TopResultsSection';
import { ImageResultCard } from './ImageResultCard';

interface ImageResultsGridProps {
    results: SearchResult[];
}

// The tray shows at most this many cited results (two rows max).
const MAX_TOP_RESULTS = 8;
const GALLERY_GAP_PX = 12;

// conv-search exposes only an orientation enum for images, and image ratios vary far more than
// footage (panoramas read as "landscape"), so these are first-paint placeholders that each card
// replaces with the thumbnail's natural ratio once it loads.
const PLACEHOLDER_ASPECT_RATIOS: Record<NonNullable<SearchResult['orientation']>, number> = {
    landscape: 3 / 2,
    portrait: 2 / 3,
    square: 1,
};

type MeasuredAspectRatios = ReadonlyMap<number, number>;

function toGalleryItems(
    results: SearchResult[],
    measured: MeasuredAspectRatios,
): Array<SearchResult & { id: number; aspectRatio: number }> {
    return results.map((result) => ({
        ...result,
        id: result.contentId,
        aspectRatio: measured.get(result.contentId) ?? PLACEHOLDER_ASPECT_RATIOS[result.orientation ?? 'landscape'],
    }));
}

export function ImageResultsGrid({ results }: ImageResultsGridProps): JSX.Element {
    const { showTopResults } = useChatSessionContext();
    const [measured, setMeasured] = useState<MeasuredAspectRatios>(new Map());
    const topResults = useTopResults()
        .filter((result) => result.contentClass === 'image')
        .slice(0, MAX_TOP_RESULTS);

    const recordAspectRatio = useCallback((contentId: number, aspectRatio: number): void => {
        setMeasured((previous) =>
            previous.get(contentId) === aspectRatio ? previous : new Map(previous).set(contentId, aspectRatio),
        );
    }, []);

    function renderGallery(items: SearchResult[], isTopResult: boolean): JSX.Element {
        return (
            <JustifiedGallery
                items={toGalleryItems(items, measured)}
                rowGapPx={GALLERY_GAP_PX}
                columnGapPx={GALLERY_GAP_PX}
            >
                {({ item, aspectRatio }) => (
                    <ImageResultCard
                        result={item}
                        aspectRatio={aspectRatio}
                        isTopResult={isTopResult}
                        onAspectRatioMeasured={recordAspectRatio}
                    />
                )}
            </JustifiedGallery>
        );
    }

    if (!showTopResults) {
        return <div className="p-6">{renderGallery(results, false)}</div>;
    }

    // The tray is additive: cited items also remain in the full results grid below.
    return (
        <div className="flex flex-col gap-2">
            {topResults.length > 0 && (
                <TopResultsSection count={topResults.length}>{renderGallery(topResults, true)}</TopResultsSection>
            )}
            <AllResultsSection count={results.length}>{renderGallery(results, false)}</AllResultsSection>
        </div>
    );
}
